/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { TreeItem, TreeView } from "@mui/lab";
import { useEffect, useState } from "react";
import { usePostRequest } from "../../../../../../../../api/helper";
import { setCoverageRequirementsClipboard } from "../../../../../../../../redux/ducks/CoverageRequirements/slice";
import { useAppDispatch } from "../../../../../../../../redux/hooks";
import ArgosFlexbox from "../../../../../../../components/Containers/ArgosFlexbox";
import { ArgosCollapseNodeIcon, ArgosExpandNodeIcon } from "../../../../../../../components/Icons/ArgosTreeIcons";
import Loader from "../../../../../../../components/Loader/Loader";
import { refreshTreeWithSanityCheck, SINGLE_DATASET_TREE_TYPE } from "../../../../constants";
import { ITreeNode, ITreeNodePopUp } from "./CoverageRequirementsTreeComponent";
import { getTreeItemColor } from "./CustomTreeItem";

export const Parent = () => {
  const [studyIdToTreeMap, setStudyIdToTreeMap] = useState<any>({});

  // fetch tree for each study
  const refreshRawTreeHandle = usePostRequest(refreshTreeWithSanityCheck);
  const [isLoadingTreeData, setIsLoadingTreeData] = useState(false);
  useEffect(() => {
    // modify this endpoint to be able to return multiple trees instead of just one
    setIsLoadingTreeData(true)
    refreshRawTreeHandle({fetchAllStudies: true}).then(r => {
      if (!r) {
        return;
      }
      // return map of study Id to tree from backend
      const _studyIdToTreeMap = r.studyIdToTreeMap;
      if (!_studyIdToTreeMap) {
        return;
      }
      setStudyIdToTreeMap(_studyIdToTreeMap);
    }).catch(e => {
      console.error(e);
    }).finally(() => {
      setIsLoadingTreeData(false)
    });
  }, []);

  const [selectedNodeId, setSelectedNodeId] = useState(null)
  const [selectedStudyId, setSelectedStudyId] = useState(null)

  const dispatch = useAppDispatch()
  useEffect(() => {
    const targetNodeId = selectedNodeId
    const targetStudyId = selectedStudyId
    const copyToStudyData = {
      targetNodeId,
      targetStudyId
    }
    dispatch(setCoverageRequirementsClipboard({copyToStudyData}))
  }, [selectedNodeId, selectedStudyId]);

  return (
    // <div>
    <div style={{height: '400px', minWidth: 200}}>
      <Loader isLoading={isLoadingTreeData}>
        {Object.keys(studyIdToTreeMap).map((studyId: string) => {
            const treeObjectData = studyIdToTreeMap[studyId]?.tree;
            return (
              <div key={studyId + 'treecontainer'}>
                <CoverageRequirementsTreeComponent2
                  key={studyId}
                  treeObjectData={treeObjectData}
                  selectedNodeId={selectedNodeId}
                  setSelectedNodeId={setSelectedNodeId}
                  selectedStudyId={selectedStudyId}
                  setSelectedStudyId={setSelectedStudyId}
                />
              </div>
            )
          }
        )
      }
      </Loader>
    </div>
  );
};

interface ITreeComponentProps {
  treeObjectData: any;
  selectedNodeId: any;
  setSelectedNodeId: any;
  selectedStudyId: any;
  setSelectedStudyId: any;
}

const CoverageRequirementsTreeComponent2 = (props: ITreeComponentProps) => {
  const {treeObjectData} = props;
  const emptyTree: ITreeNode = {nodeId: "", name: "", nodeType: ""};

  const [coverageRequirementsTree, setCoverageRequirementsTree] = useState<any>(treeObjectData);
  const studyId = coverageRequirementsTree?.studyId;

  useEffect(() => {
    setCoverageRequirementsTree(treeObjectData);
  }, [treeObjectData]);

  /**
   * Path to selected tree node (used to tell tree component which nodes to expand/collapse)
   */
  const [selectedDatasetPath, setSelectedDatasetPath] = useState<string[]>([])

  /**
   * Only update expanded nodes when clicking the expand/collapse icon
   */
  const handleToggle = (event: any, nodeIds: any) => {
    if (event.target.closest(".MuiTreeItem-iconContainer")) {
      setSelectedDatasetPath(nodeIds)
    }
  }

  const [selectedNode, setSelectedNode] = useState<any>(null)
  const [contextMenuNode, setContextMenuNode] = useState<any>(null)

  const onClickNode = (event: any, data: any) => {
    const node = data?.nodeData
    setSelectedNode(node)
    props.setSelectedNodeId(node?.nodeId)
    props.setSelectedStudyId(studyId)
  }

  const onContextMenuNode = (event: any, data: any) => {
    const node = data?.nodeData
    setContextMenuNode(node)
  }

  const extraRenderTreeParams = {
    onClickNode,
    onContextMenuNode,
  }

  return (
    <ArgosFlexbox vertical noGap style={{overflow: 'auto', height: '100%', padding: 10}}>
      <ArgosFlexbox noGap style={{overflow: 'auto', flex: 1}}>
        <TreeView
          key={coverageRequirementsTree?.studyId ?? '' + '-treeview'}
          id={coverageRequirementsTree?.studyId ?? '' + '-treeview'}
          defaultCollapseIcon={<ArgosExpandNodeIcon/>}
          defaultExpandIcon={<ArgosCollapseNodeIcon/>}

          selected={studyId === props.selectedStudyId ? props.selectedNodeId : null}
          expanded={selectedDatasetPath}
          onNodeToggle={handleToggle}

          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            // borderTop: "2px solid",
            // borderColor: theme.layout.header.backgroundColor,
            height: "100%"
          }}
        >
          {treeObjectData !== emptyTree ? renderTree(coverageRequirementsTree, SINGLE_DATASET_TREE_TYPE, extraRenderTreeParams) : ""}
        </TreeView>
      </ArgosFlexbox>
    </ArgosFlexbox>
  );
};

const renderTree = (nodes: ITreeNodePopUp, treeType: string, extraParams: any) => {
  const {onClickNode, onContextMenuNode} = extraParams;
  /**
   * TODO: Uncomment this if want to show only studies
   */
//   if (nodes.children) {
//     delete nodes.children;
//   }
  return (
    <>
      <CustomTreeItem2 nodes={nodes} onClickNode={onClickNode} onContextMenuNode={onContextMenuNode}/>
    </>
  );
};

interface ICustomTreeItemProps {
  nodes: ITreeNode;
  onClickNode: any
  onContextMenuNode: any
}

const CustomTreeItem2 = (props: ICustomTreeItemProps) => {
  const {nodes, onContextMenuNode, onClickNode} = props;
  if (!nodes) {
    console.log('nodes is null')
    return null;
  }

  const nodeType = nodes.nodeType;
  const isFolder = nodeType === "folder";
  const isStudy = nodeType === "study";
  const isDataset = nodeType === "dataset";

  /**
   * TODO: Uncomment this if you only want to show studies. Another way could be to modify the tree (remove children from the root node) before calling this component.
   */
  // if (!isStudy) {
  //   return null;
  // }
  /**
   * TODO: Uncomment this if you only want to show folders or studies
   */
  if (!(isFolder || isStudy)) {
    return null;
  }

  const nodeId = nodes.nodeId;
  const name = nodes.name;
  const treeItemColor = getTreeItemColor(nodes).treeItemColor;

  return (
    <TreeItem
      key={nodeId + 'treeitem'}
      nodeId={nodeId}
      label={name}
      style={{
        // color: treeItemColor?.length ? treeItemColor : 'black',
        color: 'black',
        whiteSpace: "nowrap",
        width: "fit-content"
      }}

      onClick={(e: any) => {
        e.stopPropagation()
        e.preventDefault()
        const data = {
          nodeData: nodes,
        }
        onClickNode(e, data)
      }}
      onContextMenu={(e: any) => {
        e.stopPropagation()
        e.preventDefault()
        const data = {
          nodeData: nodes,
        }
        onContextMenuNode(e, data)
      }}

      expandIcon={null}
    >
      {Array.isArray(nodes?.children) ?
        nodes?.children.map((node: any, index: number) => (
          <CustomTreeItem2
            key={node?.nodeId + index + 'customtreeitem'}
            nodes={node}
            onClickNode={onClickNode}
            onContextMenuNode={onContextMenuNode}
          />
        ))
        : null}
    </TreeItem>
  );
};

export default CoverageRequirementsTreeComponent2;
